import { actions } from 'Data/ReduxViewModels/Notices';
import { hasValue } from 'Lib/Any';
import deepEquals from 'Lib/Object/deepEquals';
import { copyTextToClipboard } from 'Lib/clipboard';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef } from 'react';
import { MdClose as IconClose } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../Button';
import { MessageDialog } from '../Dialog';
import './Notice.scss';

type NoticeProps = {
  noticeId: string;
};

const Notice: React.FC<NoticeProps> = ({ noticeId }) => {
  const HIDE_DELAY_IN_MS = 5000;

  const dispatch = useDispatch();
  const deleteNotice = useCallback(() => dispatch(actions.deleteNotice({ id: noticeId })), [dispatch, noticeId]);
  const notice = useSelector((s) => s.notices.noticeData[noticeId], deepEquals);

  const timer = useRef<NodeJS.Timeout>();

  const stopHideTimeout = useCallback(() => {
    if (timer?.current) clearTimeout(timer.current);
  }, [timer]);

  const handleClose = useCallback(() => {
    stopHideTimeout();
    deleteNotice();
  }, [deleteNotice, stopHideTimeout]);

  const startHideTimeout = useCallback(() => {
    timer.current = setTimeout(() => {
      handleClose();
    }, HIDE_DELAY_IN_MS);
  }, [handleClose]);

  const isAutoHidingNotice = !(notice?.severity === 'error');
  useEffect(() => {
    if (isAutoHidingNotice) startHideTimeout();
  }, [isAutoHidingNotice, startHideTimeout]);

  if (!hasValue(notice)) return null;

  const { severity, message, errorInfo, additionalDetails, actionLink, link } = notice!;

  const handleMouseEnter = () => {
    if (isAutoHidingNotice) stopHideTimeout();
  };

  const handleMouseLeave = () => {
    if (isAutoHidingNotice) startHideTimeout();
  };

  const cx = classNames('Notice', {
    [`Notice-${severity}`]: severity,
  });

  return (
    <div className={cx} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div className="Notice-Content">
        <span className="Notice-Message">
          {message + ' '}
          {errorInfo && (
            <div className="Notice-Error">
              <br />
              <p>Please provide the information below to your administrator to help troubleshoot the problem.</p>
              <p>Server Time: {errorInfo.serverTime}</p>
              Error ID: {errorInfo.id}
            </div>
          )}
          {additionalDetails && (
            <span>
              <a
                href="about:blank"
                onClick={(e) => {
                  e.preventDefault();
                  copyTextToClipboard(additionalDetails);
                }}
              >
                Copy details to clipboard
              </a>
            </span>
          )}
          {actionLink && (
            <span>
              <a
                href="about:blank"
                onClick={(e) => {
                  e.preventDefault();
                  handleClose();
                  dispatch(actionLink?.action);
                }}
              >
                {actionLink.linkText}
              </a>
            </span>
          )}
          {link && (
            <>
              {window.qest.isFrame ? (
                <MessageDialog
                  messageTitle="Feature Unavailable"
                  messageText="This feature is unavailable outside of the QEST Web App."
                  alwaysEnabled
                >
                  <Button subtle primary alwaysEnabled noPadding className="Notice-Link-Button">
                    <span className="Notice-Link-Text">{link.linkText}</span>
                  </Button>
                </MessageDialog>
              ) : (
                <span>
                  <a
                    href={link.url}
                    onClick={(e) => {
                      handleClose();
                    }}
                  >
                    <br />
                    <span className="Notice-Link-Text">{link.linkText}</span>
                  </a>
                </span>
              )}
            </>
          )}
        </span>
        <span className="Notice-CloseButton">
          <Button
            onClick={handleClose}
            primary={severity === 'info'}
            success={severity === 'success'}
            warning={severity === 'warning'}
            error={severity === 'error'}
            subtle
          >
            <IconClose />
          </Button>
        </span>
      </div>
    </div>
  );
};

export { Notice };
export default Notice;
